import { useMemo } from 'react';

import {
  useGetSharedWithMeFoldersQuery,
  useGetSharedWithMeProjectsQuery,
  useGetTrashAssetsQuery,
  useGetTrashFoldersQuery,
  useGetTrashPresentationsQuery,
} from '../services';
import { FolderManagerItemTypes } from '../interfaces';

interface IUseHierarchyData {
  slug: string | undefined;
  rootFolderId: number | undefined | null;
  folderManagerHierarchyData: any;
}

// Some endpoints return data that's not structured as folders,
// but we need to present it in a folder-like format.
// This logic handles those cases to ensure consistency in how the data is displayed.
export const useHierarchyData = ({ slug, rootFolderId, folderManagerHierarchyData }: IUseHierarchyData) => {
  const { data: sharedFoldersData } = useGetSharedWithMeFoldersQuery(void 0, { skip: slug !== 'shared' });
  const { data: sharedWorkspacesData } = useGetSharedWithMeProjectsQuery(void 0, { skip: slug !== 'shared' });
  const { data: trashFoldersData } = useGetTrashFoldersQuery(void 0, { skip: slug !== 'trash' });
  const { data: trashAssetsData } = useGetTrashAssetsQuery(void 0, { skip: slug !== 'trash' });
  const { data: trashPresentationsData } = useGetTrashPresentationsQuery(void 0, { skip: slug !== 'trash' });

  //@todo: return proper object that folder manager is using and remove
  // creating this object from folder manager as it will be redundand
  // eg. { folders: [], assets: [] }
  const data = useMemo(() => {
    if (rootFolderId) {
      return folderManagerHierarchyData || [];
    }

    if (slug === 'trash') {
      return [...(trashFoldersData || []), ...(trashPresentationsData || []), ...(trashAssetsData || [])];
    }

    if (slug === 'shared') {
      const mappedWorkspaces = (sharedWorkspacesData || []).map((workspace) => ({
        ...workspace,
        type: FolderManagerItemTypes.WORKSPACE,
      }));

      return [...(mappedWorkspaces || []), ...(sharedFoldersData || [])];
    }

    return folderManagerHierarchyData || [];
  }, [
    slug,
    rootFolderId,
    folderManagerHierarchyData,
    trashFoldersData,
    trashAssetsData,
    trashPresentationsData,
    sharedFoldersData,
    sharedWorkspacesData,
  ]);

  return { data: data };
};
